// Rechen- und Prüflogik der Geldabholung. Reine Funktionen ohne React/Browser – läuft
// ausschließlich auf dem Gerät von Philipp/Daniel über die ENTSCHLÜSSELTEN Einträge
// (der Server kennt keine dieser Zahlen, siehe backend/lib/cash_vault.js).
//
// Eintrag (Klartext, nur im Browser):
//   { id, date: 'YYYY-MM-DD' (Wiesn-Tag), time: 'HH:MM', station, by,
//     rolls: [{ from, to }],   // Wertmarken seit der letzten Abholung: erste–letzte verkaufte Nummer je Rolle
//     maxTag,                  // höchste erreichte Garderobennummer (nur Richtwert – Nummern werden neu vergeben)
//     sollManual,              // nur WG: € Umsatz laut Kasse seit der letzten Abholung (statt Marken × Preis)
//     brought, left, counted,  // € Wechselgeld gebracht · € Restgeld in der Kasse danach · € gezählt (später)
//     note }
//
// Rollen sind in der Regel komplette Tausender-Blöcke (x000–x999); angebrochene Rollen kommen
// selten vor → nur Hinweis, kein Fehler. Eine Station kann zwei Rollen gleichzeitig haben
// (deshalb Abgleich je Tausender-Block, nicht je Station). G3 ist die Zusatzstation in G1.

export const STATIONS = ['G1', 'G3', 'G5', 'G8', 'G10', 'WG'];
// Wiesn-Garten: keine Wertmarken. Soll = optional eingetragener „Umsatz laut Kasse" (sollManual), sonst keine Differenz.
export const NO_MARKS = ['WG'];
export const PRICE = 5; // € je Wertmarke/Gepäckstück
export const TOLERANCE = 5; // € – bis dahin gilt eine Differenz als „passt"

const num = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v));
const cents = (v) => Math.round((num(v) || 0) * 100);
const block = (n) => Math.floor(n / 1000);

// „1.234,50" / „1234.5" / „1234" → Zahl (oder null)
export function parseMoney(s) {
  if (s === null || s === undefined) return null;
  let t = String(s).trim().replace(/[€\s]/g, '');
  if (!t) return null;
  if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.');
  const n = Number(t);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
}

// Minuten seit 00:00 des Wiesn-Tags: Abholungen nach Mitternacht (bis 06:00) zählen zum Vortag
export function minutesOf(time) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(time || ''));
  if (!m) return 0;
  const h = Number(m[1]);
  return (h < 6 ? h + 24 : h) * 60 + Number(m[2]);
}
const sortKey = (e) => `${e.date} ${String(minutesOf(e.time)).padStart(5, '0')} ${e.id}`;

export const rollCount = (r) => (num(r.from) === null || num(r.to) === null ? 0 : Number(r.to) - Number(r.from) + 1);
export const entryMarks = (e) => (e.rolls || []).reduce((s, r) => s + Math.max(0, rollCount(r)), 0);

// Offene (nicht zu Ende verkaufte) Rollen einer Station → Vorschlag für das nächste „von"
export function openRolls(entries, station, beforeKey = null) {
  const last = new Map(); // Block → letzte verkaufte Nummer
  entries.filter((e) => e.station === station && (!beforeKey || sortKey(e) < beforeKey)).sort((a, b) => (sortKey(a) < sortKey(b) ? -1 : 1))
    .forEach((e) => (e.rolls || []).forEach((r) => { if (rollCount(r) > 0) last.set(block(r.to), Math.max(last.get(block(r.to)) ?? -1, Number(r.to))); }));
  return [...last.entries()].filter(([, to]) => to % 1000 !== 999).map(([b, to]) => ({ block: b, next: to + 1, left: 999 - (to % 1000) })).sort((a, b) => a.block - b.block);
}

// Alle Einträge prüfen. Liefert je Eintrag Kennzahlen + Warnungen, dazu Summen je Station und je Tag.
export function analyze(allEntries) {
  const entries = [...allEntries].sort((a, b) => (sortKey(a) < sortKey(b) ? -1 : 1));
  const rows = new Map();     // id → Auswertung
  const lastTo = new Map();   // `${station}|${block}` → letzte verkaufte Nummer
  const prevOf = new Map();   // station → vorheriger Eintrag
  const stations = {};        // station → Gesamtsummen (ganze Wiesn – „gesamt prüfen", weil das Wechselgeld schwankt)
  const days = {};            // `${date}|${station}` → Tagessummen

  for (const e of entries) {
    const warn = []; const info = [];
    const st = e.station;
    const prev = prevOf.get(st) || null;

    // --- Wertmarken: Anschluss an die letzte Abholung, je Rolle (Tausender-Block) ---
    for (const r of e.rolls || []) {
      const from = num(r.from), to = num(r.to);
      if (from === null || to === null) { warn.push('Wertmarken: von und bis ausfüllen.'); continue; }
      if (to < from) { warn.push(`Wertmarken ${from}–${to}: „bis" ist kleiner als „von".`); continue; }
      if (block(from) !== block(to)) { warn.push(`Wertmarken ${from}–${to} gehen über zwei Rollen – bitte als zwei Zeilen eintragen (alte Rolle bis …999, neue Rolle ab …000).`); continue; }
      const k = `${st}|${block(from)}`;
      if (lastTo.has(k)) {
        const exp = lastTo.get(k) + 1;
        if (from > exp) warn.push(`Lücke: ${exp}–${from - 1} fehlen (${from - exp} Marken = ${(from - exp) * PRICE} €). Letzte Abholung endete bei ${exp - 1}.`);
        if (from < exp) warn.push(`Überlappung: ${from}–${Math.min(to, exp - 1)} wurden schon abgerechnet (letzte Abholung endete bei ${exp - 1}).`);
      } else if (from % 1000 > 1) {
        info.push(`Rolle beginnt bei ${from} statt bei ${block(from) * 1000} – angebrochene Rolle oder erste Erfassung? Sonst fehlen ${from % 1000} Marken (${(from % 1000) * PRICE} €).`);
      }
      lastTo.set(k, Math.max(lastTo.get(k) ?? -1, to));
    }

    const noMarks = NO_MARKS.includes(st);
    const marks = noMarks ? 0 : entryMarks(e);
    const sollKnown = !noMarks || num(e.sollManual) !== null;
    const soll = noMarks ? (num(e.sollManual) || 0) : marks * PRICE;

    // --- Höchste Garderobennummer: kann nicht stärker steigen, als Marken verkauft wurden ---
    const tag = num(e.maxTag); const prevTag = prev ? num(prev.maxTag) : null;
    if (tag !== null && prevTag !== null && tag - prevTag > marks) {
      info.push(`Höchste Garderobennummer +${tag - prevTag}, aber nur ${marks} Marken verkauft – Gepäck ohne Marke angenommen? (Richtwert)`);
    }

    // --- Geld: exakt nur, wenn Restgeld vorher UND nachher bekannt ist ---
    const counted = num(e.counted), left = num(e.left), brought = num(e.brought) || 0;
    const prevLeft = prev ? num(prev.left) : 0; // vor der ersten Abholung: leere Kasse, Wechselgeld steht unter „gebracht"
    let diff = null; let diffExact = false;
    if (counted !== null && sollKnown) {
      if (left !== null && prevLeft !== null) { diff = (cents(counted) + cents(left) - cents(prevLeft) - cents(brought) - cents(soll)) / 100; diffExact = true; }
      else diff = (cents(counted) - cents(brought) - cents(soll)) / 100; // grob: Wechselgeld-Schwankung steckt mit drin
    }
    if (diffExact && Math.abs(diff) > TOLERANCE) warn.push(`Differenz ${diff > 0 ? '+' : ''}${diff.toFixed(2)} € (gezählt + Restgeld gegenüber ${noMarks ? 'Umsatz laut Kasse' : `Marken × ${PRICE} €`}).`);

    // --- Tempo: Marken je Stunde seit der letzten Abholung am selben Tag ---
    let perHour = null;
    if (prev && prev.date === e.date) { const mins = minutesOf(e.time) - minutesOf(prev.time); if (mins >= 15) perHour = Math.round((marks / mins) * 60); }

    rows.set(e.id, { marks, soll, sollKnown, noMarks, diff, diffExact, perHour, warn, info });

    const S = stations[st] || (stations[st] = { marks: 0, soll: 0, counted: 0, brought: 0, uncounted: 0, noSoll: 0, noMarks, lastLeft: null, lastLeftKnown: false, pickups: 0, warnings: 0 });
    S.marks += marks; S.soll += soll; S.brought += brought; S.pickups += 1; S.warnings += warn.length;
    // Reine Wechselgeld-Lieferung (keine Marken, nichts mitgenommen) gilt nicht als „noch nicht gezählt"
    if (counted !== null) S.counted += counted; else if (marks > 0 || noMarks) S.uncounted += 1;
    if (!sollKnown) S.noSoll += 1;
    S.lastLeft = left; S.lastLeftKnown = left !== null;

    const dk = `${e.date}|${st}`;
    const D = days[dk] || (days[dk] = { date: e.date, station: st, marks: 0, soll: 0, counted: 0, brought: 0, uncounted: 0, noSoll: 0, noMarks, pickups: 0, startLeft: prevLeft, endLeft: null });
    D.marks += marks; D.soll += soll; D.brought += brought; D.pickups += 1; D.endLeft = left;
    if (counted !== null) D.counted += counted; else if (marks > 0 || noMarks) D.uncounted += 1;
    if (!sollKnown) D.noSoll += 1;

    prevOf.set(st, e);
  }

  // Gesamtprüfung je Station: Σ gezählt − Σ gebracht + aktuelles Restgeld  gegen  Σ Marken × Preis
  Object.values(stations).forEach((S) => {
    S.exact = S.uncounted === 0 && S.lastLeftKnown && S.noSoll === 0;
    S.ist = (cents(S.counted) - cents(S.brought) + cents(S.lastLeft)) / 100;
    S.diff = Math.round((S.ist - S.soll) * 100) / 100;
  });
  Object.values(days).forEach((D) => {
    D.exact = D.uncounted === 0 && D.endLeft !== null && D.startLeft !== null && D.noSoll === 0;
    D.ist = (cents(D.counted) - cents(D.brought) + cents(D.endLeft) - cents(D.startLeft)) / 100;
    D.diff = Math.round((D.ist - D.soll) * 100) / 100;
  });

  return { sorted: entries, rows, stations, days: Object.values(days).sort((a, b) => (a.date === b.date ? STATIONS.indexOf(a.station) - STATIONS.indexOf(b.station) : a.date < b.date ? 1 : -1)) };
}

// CSV für den Steuerberater (wird lokal erzeugt und heruntergeladen – geht nicht über den Server)
export function toCsv(allEntries) {
  const { sorted, rows } = analyze(allEntries);
  const de = (n) => (n === null || n === undefined ? '' : String(n).replace('.', ','));
  const head = ['Datum', 'Uhrzeit', 'Station', 'Abholer', 'Wertmarken', 'Anzahl Marken', 'Soll EUR', 'Gezaehlt EUR', 'Wechselgeld gebracht EUR', 'Restgeld Kasse EUR', 'Differenz EUR', 'Hoechste Garderobennr', 'Notiz'];
  const lines = sorted.map((e) => {
    const r = rows.get(e.id);
    return [e.date, e.time, e.station, e.by || '', r.noMarks ? '' : (e.rolls || []).map((x) => `${x.from}-${x.to}`).join(' + '), r.noMarks ? '' : r.marks, r.sollKnown ? de(r.soll) : '', de(num(e.counted)), de(num(e.brought)), de(num(e.left)),
      r.diffExact ? de(r.diff) : '', e.maxTag ?? '', String(e.note || '').replace(/[\r\n;]+/g, ' ')].join(';');
  });
  return '﻿' + [head.join(';'), ...lines].join('\r\n');
}
